import { Mutex } from 'async-mutex';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo, useRef } from 'react';

import { TrackingEvents, sendLoggingEvents } from '../events';
import type {
  RemoteMediaDelegator,
  RemoteParticipant,
  RemoteParticipantMediaEventHandler,
  RemoteVideoDelegator,
} from '../video-sdk';
import {
  MediaSource,
  remoteMediaByParticipantIdAtom,
  remoteParticipantToDiagnosticInfo,
  remoteScreenShareParticipantAtom,
  remoteScreenShareVideoDelegatorAtom,
} from '../video-sdk';
import {
  addMediaToParticipant,
  remoteMediaDelagatorToDiagnosticInfo,
  remoteMediaStatusByParticipantIdAtom,
  removeMediaFromParticipant,
} from '../video-sdk/twilio';
import { useVideoQualityCallbacks } from './controls';
import { useRemoteMediaEventListeners } from './useRemoteMediaEventListeners';
import { gracefullyHandleMediaEvents } from './utils';

export type UseRemoteVideoProps = {
  remoteParticipant: RemoteParticipant;
  onVideoSubscribed?: RemoteParticipantMediaEventHandler;
  onVideoUnsubscribed?: RemoteParticipantMediaEventHandler;
};

const remoteVideoEventMutex = new Mutex();

function useGetRemoteVideoEventListeners({
  getVideoElement,
  remoteParticipant,
  onVideoSubscribed,
  onVideoUnsubscribed,
}: UseRemoteVideoProps & {
  getVideoElement: () => HTMLVideoElement | null;
}) {
  const setRemoteScreenShareParticipant = useSetAtom(remoteScreenShareParticipantAtom);
  const setRemoteScreenShareVideoDelegator = useSetAtom(remoteScreenShareVideoDelegatorAtom);
  const getActiveRemoteScreenShare = useAtomCallback(
    useCallback((get) => {
      return {
        participant: get(remoteScreenShareParticipantAtom),
        delegator: get(remoteScreenShareVideoDelegatorAtom),
      };
    }, []),
  );

  const setVideoTrackState = useSetAtom(remoteMediaStatusByParticipantIdAtom);
  const setRemoteTracks = useSetAtom(remoteMediaByParticipantIdAtom);
  const { degradeVideoQuality, revertVideoQuality } = useVideoQualityCallbacks();

  return useMemo(
    () => {
      if (!remoteParticipant) return;
      return gracefullyHandleMediaEvents(remoteVideoEventMutex, {
        mediaSubscribed: async (remoteMediaDelegator, ...otherArgs) => {
          const element = getVideoElement();
          if (!isRemoteVideoDelegator(remoteMediaDelegator) || !element) return;
          const source = remoteMediaDelegator.source;

          if (source === MediaSource.Screen && remoteParticipant) {
            // TODO: We may be able to get rid of setting the screen share track
            // now that we're setting all remote video tracks
            setRemoteScreenShareVideoDelegator(remoteMediaDelegator);
            setRemoteScreenShareParticipant(remoteParticipant);
          }

          if (source === MediaSource.Camera) {
            remoteMediaDelegator.attach(element);
            onVideoSubscribed?.(remoteMediaDelegator, ...otherArgs);
            setVideoTrackState((prev) => ({
              ...prev,
              [remoteParticipant.identity]: {
                ...prev[remoteParticipant.identity],
                isVideoOff: !remoteMediaDelegator.isEnabled,
              },
            }));
          }

          setRemoteTracks((prev) => addMediaToParticipant(prev, remoteParticipant.identity, remoteMediaDelegator));

          await degradeVideoQuality();

          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_SUBSCRIBED,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteMediaDelegator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: 'info',
              message: 'Remote participant video subscribed',
            },
          );
        },
        mediaEnabled: (remoteMediaDelegator) => {
          if (remoteMediaDelegator.kind !== 'video') return;
          setVideoTrackState((prev) => ({
            ...prev,
            [remoteParticipant.identity]: {
              ...prev[remoteParticipant.identity],
              isVideoOff: false,
            },
          }));
        },
        mediaUnsubscribed: async (remoteMediaDelegator, ...otherArgs) => {
          const element = getVideoElement();
          if (!isRemoteVideoDelegator(remoteMediaDelegator) || !element) return;
          const source = remoteMediaDelegator.source;

          if (source === MediaSource.Screen && remoteParticipant) {
            const { participant, delegator } = getActiveRemoteScreenShare();

            // only clear screen share if this participant is the active sharer
            if (participant?.sid !== remoteParticipant.sid || delegator?.sid !== remoteMediaDelegator.sid) {
              return;
            }

            setRemoteScreenShareParticipant(undefined);
            setRemoteScreenShareVideoDelegator(undefined);
          }
          if (source === MediaSource.Camera) {
            remoteMediaDelegator.detach(element);
            onVideoUnsubscribed?.(remoteMediaDelegator, ...otherArgs);
            setVideoTrackState((prev) => ({
              ...prev,
              [remoteParticipant.identity]: {
                ...prev[remoteParticipant.identity],
                isVideoOff: true,
              },
            }));
          }

          setRemoteTracks((prev) => removeMediaFromParticipant(prev, remoteParticipant.identity, remoteMediaDelegator));

          await revertVideoQuality();

          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_UNSUBSCRIBED,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteMediaDelegator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: 'info',
              message: 'Remote participant video unsubscribed',
            },
          );
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getVideoElement, onVideoSubscribed, onVideoUnsubscribed, remoteParticipant],
  );
}

export function useRemoteVideo({ remoteParticipant, ...otherProps }: UseRemoteVideoProps) {
  const remoteParticipantVideoRef = useRef<HTMLVideoElement>(null);
  const getVideoElement = useCallback(() => remoteParticipantVideoRef.current, []);

  const remoteVideoEventListeners = useGetRemoteVideoEventListeners({
    ...otherProps,
    getVideoElement,
    remoteParticipant,
  });

  useRemoteMediaEventListeners({
    mediaKind: 'video',
    remoteParticipant,
    remoteMediaEventListeners: remoteVideoEventListeners,
  });

  return remoteParticipantVideoRef;
}

// type guard
function isRemoteVideoDelegator(delegator: RemoteMediaDelegator): delegator is RemoteVideoDelegator {
  return delegator.kind === 'video';
}
